<template>
  <b-modal
    id="create-evacuation-diagram-modal"
    title="EvacuationDiagram Create Modal"
    centered
    hide-footer
    size="lg"
    @hidden="reset"
    :no-close-on-esc="true"
    :no-close-on-backdrop="true"
  >
    <template #modal-title>
      <h2 class="m-0">Enter Evacuation Diagram</h2>
    </template>
    <validation-observer ref="createEvacuationDiagramValidation">
      <b-form @submit.prevent enctype="multipart/form-data">
        <validation-provider
          rules="required"
          v-slot="{ errors }"
          name="Site"
        >
        <b-form-group label-for="site">
          <template #label>
            Site <span class="text-danger">*</span>
          </template>
            <v-select
              label="name"
              v-model="site"
              placeholder="Select Site"
              :options="sites"
              autoscroll
              :clearable="true"
              @input="fetchBuildings()"
              required
            ></v-select>
          </b-form-group>
          <p class="text-danger">{{ errors[0] }}</p>
        </validation-provider>

        <validation-provider
          rules="required"
          v-slot="{ errors }"
          name="Building"
        >
        <b-form-group label-for="building">
          <template #label>
            Building <span class="text-danger">*</span>
          </template>
            <v-select
              label="name"
              v-model="building"
              placeholder="Select Site"
              :options="buildings"
              autoscroll
              :clearable="true"
              @input="fetchFloors()"
              required
            ></v-select>
          </b-form-group>
          <p class="text-danger">{{ errors[0] }}</p>
        </validation-provider>

        <validation-provider
          rules="required"
          v-slot="{ errors }"
          name="Floor"
        >
          <b-form-group label-for="floor">
            <template #label>
              Floor <span class="text-danger">*</span>
            </template>
            <v-select
              label="floor_no"
              v-model="floor"
              placeholder="Select Floor"
              :options="floors"
              autoscroll
              :clearable="true"
              required
            ></v-select>
          </b-form-group>
          <p class="text-danger">{{ errors[0] }}</p>
        </validation-provider>

        <validation-provider
          rules="required"
          v-slot="{ errors }"
          name="Installation Date"
        >
          <b-form-group label-for="installation_date">
            <template #label>
              Installation Date <span class="text-danger">*</span>
            </template>
            <b-form-datepicker id="installation_date" v-model="installation_date" class="mb-2"></b-form-datepicker>
          </b-form-group>
          <p class="text-danger">{{ errors[0] }}</p>
        </validation-provider>

        <validation-provider
          rules="required"
          v-slot="{ errors }"
          name="Expiry Date"
        >
          <b-form-group label-for="expiry_date">
            <template #label>
              Expiry Date <span class="text-danger">*</span>
            </template>
            <b-form-datepicker id="expiry_date" v-model="expiry_date" class="mb-2"></b-form-datepicker>
          </b-form-group>
          <p class="text-danger">{{ errors[0] }}</p>
        </validation-provider>


        <validation-provider
          rules="required"
          v-slot="{ errors }"
          name="Evacuation Diagram"
        >
          <b-form-group
            class="form-group"
            id="input-group-2"
            label-for="input-2"
            description="Upload an Evacuation Diagram in PNG, JPG, JPEG, or PDF format."
          >
            <template #label>
              Evacuation Diagram: <span class="text-danger">*</span>
            </template>
            <b-form-file
              ref="evacuationDiagramInput"
              id="evacuationDiagram"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              required
              accept=".png,.jpg,.jpeg,.pdf"
              plain
              v-model="evacuation_diagram"
            ></b-form-file>
            <p class="text-danger">{{ errors[0] }}</p>
          </b-form-group>
        </validation-provider>


        <b-form-group class="mt-2 text-right">
          <b-button
            type="submit"
            variant="primary"
            pill
            class="mr-1"
            @click="validateAndSubmit"
          >
            Save
          </b-button>
          <b-button
            type="reset"
            variant="outline-secondary"
            @click="reset"
            pill
          >
            Reset
          </b-button>
        </b-form-group>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import util from "@/util.js";
import { quillEditor } from "vue-quill-editor";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";


export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    quillEditor,
  },
  mixins: [util],
  data() {
    return {
      required,
      name: "",
      regex: /^[a-zA-Z ]*$/,
      site: null,
      building: null,
      floor: null,
      sites: [],
      buildings: [],
      floors : [],
      expiry_date: "",
      installation_date: "",
      evacuation_diagram: null,      
    };
  },
  methods: {
    ...mapActions({
      createEvacuationDiagram: "appData/createEvacuationDiagram",
      getFloorsDDL: "appData/getFloorsDDL",
      getBuildingsDDL: "appData/getBuildingsDDL",
      getSitesDDL: "appData/getSitesDDL",
    }),

    async validateAndSubmit() {
      // Validate the whole form before submission
      const isValid = await this.$refs.createEvacuationDiagramValidation.validate();
      if (isValid) {
        this.submit();
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Form submission failed",
            text: "Please ensure all required fields are filled correctly.",
            variant: "danger",
            icon: "AlertCircleIcon",
          },
        });
      }
    },

    async submit() {
      try {
        const formData = new FormData();
        formData.append("site", this.site.id);
        formData.append("building", this.building.id);
        formData.append("floor", this.floor.id);
        formData.append("installation_date", this.installation_date);
        formData.append("expiry_date", this.expiry_date);
        formData.append("evacuation_diagram", this.evacuation_diagram);
        formData.append("created_by", this.getLoggedInUser.id);
        formData.append("updated_by", this.getLoggedInUser.id);
        const res = await this.createEvacuationDiagram(formData);
        if (res.status === 201) {
          this.$toast(
            {
              component: ToastificationContent,
              props: {
                title: "Evacuation Diagram Created Successfully",
                variant: "success",
                icon: "BellIcon",
              },
            },
            {
              position: "top-right",
            }
          );
          this.reset();
          this.$nextTick(() => {
            this.$bvModal.hide("create-evacuation-diagram-modal");
          });
          this.$emit("modalClosed");
        }
      } catch (error) {
        this.displayError(error);
      }
    },

    validateFileType(event, fileType) {
      const file = event.target.files[0];
      if (!file) return;

      if (fileType === 'evacuation_diagram') {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          event.target.value = null;
          if (this.$refs.displayImageInput) {
            this.$refs.displayImageInput.$el.setCustomValidity('Please upload a PNG, JPG, or JPEG image file.');
            this.$refs.displayImageInput.$el.reportValidity();
          }
        } else {
          if (this.$refs.displayImageInput) {
            this.$refs.displayImageInput.$el.setCustomValidity('');
          }
        }
      }
    },

    async fetchBuildings(){
      const buildings =  await this.getBuildingsDDL();
      this.buildings = buildings.data;
      console.log(this.buildings);
      this.building = "";
      this.floor = "";
       if (!this.site) {
        this.building = [];
        return;
      }
      this.buildings = this.buildings.filter(building => building.site === this.site.id);      
    },

    async fetchFloors(){
      const floors =  await this.getFloorsDDL();
      this.floors = floors.data;
      this.floor = "";
       if (!this.building) {
        this.floors = [];
        return;
      }
      this.floors = this.floors.filter(floor => floor.building === this.building.id);   

    },

    reset() {
      this.name = "";
      this.location = "";
      this.description = "";
      this.files = null;
      this.status = "";
    },
  },
  computed: {
    ...mapGetters({ getLoggedInUser: "appData/getLoggedInUser" }),
  },
  async mounted(){
    const sites =  await this.getSitesDDL();
    this.sites = sites.data;
  },
};
</script>

<style></style>
